import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { CompanySidebar } from '../components/Dashboard/CompanySidebar';
import {
    Search,
    Download,
    Linkedin,
    Mail,
    Users,
    ExternalLink,
    CheckCircle,
    Briefcase,
    MapPin,
    ChevronLeft,
    ChevronRight,
    AlertCircle,
    Sparkles,
    X,
    Copy,

    GraduationCap,
    Rocket,
    Building2,
    Lightbulb,
    Star,
    Eye
} from 'lucide-react';

interface CompanyHistory {
    name: string;
    role: string;
    tier?: string;
    start_year?: number;
    end_year?: number; // null/undefined means "Present"
}

interface EducationHistory {
    uni?: string;
    degree?: string;
    field?: string;
    tier?: string;
    start_year?: number;
    end_year?: number;
}

interface PurchasedCandidate {
    id: string;
    candidateId: string;
    name: string;
    email: string;
    jobTitle: string;
    jobId: string | null;
    purchaseType: 'BULK' | 'SINGLE';
    purchasedAt: string;
    city: string;
    country: string;
    role: string;
    seniority: string;
    summary: string;
    skills: string[];
    linkedinUrl: string;
    cvUrl: string | null;
    yearsExperience?: number;
    hasStartupExperience?: boolean;
    hasFoundedCompany?: boolean;
    hasSideProjects?: boolean;
    // Education fields
    university?: string;
    degree?: string;
    educations?: EducationHistory[]; // All education entries for timeline
    // Company fields
    currentCompany?: string;
    companies?: CompanyHistory[];
}

// Skeleton for loading state
const TableSkeleton = () => (
    <div className="animate-pulse">
        {/* Header Skeleton */}
        <div className="hidden lg:grid grid-cols-12 gap-4 px-6 py-4 bg-slate-50 border-b border-slate-100">
            <div className="col-span-3 h-3 bg-slate-200 rounded w-20" />
            <div className="col-span-2 h-3 bg-slate-200 rounded w-16" />
            <div className="col-span-2 h-3 bg-slate-200 rounded w-16" />
            <div className="col-span-2 h-3 bg-slate-200 rounded w-16" />
            <div className="col-span-1 h-3 bg-slate-200 rounded w-14" />
            <div className="col-span-2 h-3 bg-slate-200 rounded w-14 ml-auto" />
        </div>
        {/* Row Skeletons */}
        <div className="divide-y divide-slate-100">
            {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="hidden lg:grid grid-cols-12 gap-4 px-6 py-5 items-center">
                    <div className="col-span-3 flex items-center gap-4">
                        <div className="w-12 h-12 bg-slate-200 rounded-full" />
                        <div className="space-y-2">
                            <div className="h-4 bg-slate-200 rounded w-28" />
                            <div className="h-3 bg-slate-100 rounded w-36" />
                        </div>
                    </div>
                    <div className="col-span-2">
                        <div className="h-4 bg-slate-100 rounded w-24" />
                    </div>
                    <div className="col-span-2">
                        <div className="h-4 bg-slate-100 rounded w-20" />
                    </div>
                    <div className="col-span-2">
                        <div className="h-4 bg-slate-100 rounded w-24" />
                    </div>
                    <div className="col-span-1">
                        <div className="h-4 bg-slate-100 rounded w-16" />
                    </div>
                    <div className="col-span-2 flex justify-end gap-2">
                        <div className="h-8 bg-slate-100 rounded w-16" />
                        <div className="h-8 w-8 bg-slate-100 rounded" />
                    </div>
                </div>
            ))}
        </div>
        {/* Mobile Skeleton */}
        <div className="lg:hidden space-y-4 p-4">
            {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="p-4 bg-slate-50 rounded-xl space-y-3">
                    <div className="flex items-start gap-3">
                        <div className="w-14 h-14 bg-slate-200 rounded-xl" />
                        <div className="flex-1 space-y-2">
                            <div className="h-4 bg-slate-200 rounded w-32" />
                            <div className="h-3 bg-slate-100 rounded w-40" />
                            <div className="h-3 bg-slate-100 rounded w-24" />
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <div className="h-6 bg-slate-100 rounded w-20" />
                        <div className="h-6 bg-slate-100 rounded w-16" />
                    </div>
                </div>
            ))}
        </div>
    </div>
);

// Candidate Detail Modal for Purchased Candidates (All details exposed)
interface CandidateDetailModalProps {
    candidate: PurchasedCandidate | null;
    onClose: () => void;
}

const CandidateDetailModal: React.FC<CandidateDetailModalProps> = ({ candidate, onClose }) => {
    if (!candidate) return null;

    // Helper to format date range for timeline
    const formatDateRange = (startYear?: number, endYear?: number) => {
        const start = startYear ? startYear.toString() : '';
        const end = endYear ? endYear.toString() : 'Present';
        if (!start && !endYear) return ''; // No dates at all
        if (!start) return end; // Only end year
        return `${start} — ${end}`;
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="relative w-full max-w-5xl max-h-[90vh] bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col animate-scale-in">
                {/* Header with gradient */}
                <div className="relative flex-shrink-0">
                    <div className="h-28 md:h-36 bg-gradient-to-r from-emerald-500 to-teal-600" />

                    {/* Close button */}
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 p-2.5 bg-white/20 hover:bg-white/30 rounded-full transition-colors backdrop-blur-md group z-10"
                    >
                        <X className="w-5 h-5 text-white group-hover:scale-110 transition-transform" />
                    </button>

                    {/* Header Content */}
                    <div className="px-6 md:px-10 absolute -bottom-6 w-full flex justify-between items-end">
                        <div className="flex items-end gap-4 md:gap-6">
                            {/* Avatar */}
                            <div className="w-20 h-20 md:w-24 md:h-24 bg-white p-1 md:p-1.5 rounded-2xl shadow-xl flex-shrink-0">
                                <div className="w-full h-full bg-gradient-to-br from-slate-700 to-slate-900 rounded-xl flex items-center justify-center text-white font-bold text-2xl md:text-3xl">
                                    {candidate.name.split(' ').map(n => n[0]).join('')}
                                </div>
                            </div>

                            {/* Name, Role and Current Company - visible on desktop */}
                            <div className="mb-6 md:mb-8 hidden md:block text-white drop-shadow-md">
                                <h2 className="text-2xl md:text-3xl font-bold">{candidate.name}</h2>
                                <p className="text-base md:text-lg opacity-90 font-medium">{candidate.role || 'Professional'}</p>
                                {candidate.currentCompany && (
                                    <p className="text-sm opacity-80 flex items-center gap-1.5 mt-0.5">
                                        <Building2 className="w-4 h-4" />
                                        {candidate.currentCompany}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Unlocked Badge */}
                        <div className="hidden md:flex items-center gap-2 bg-white px-4 py-2.5 rounded-xl shadow-lg border border-slate-100 mb-2">
                            <CheckCircle className="w-5 h-5 text-emerald-600" />
                            <span className="text-sm font-semibold text-slate-900">Unlocked</span>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto pt-10 px-6 md:px-10 pb-8">
                    {/* Mobile Header */}
                    <div className="md:hidden mb-6 space-y-3">
                        <h2 className="text-2xl font-bold text-slate-900">{candidate.name}</h2>
                        <p className="text-lg text-slate-600">{candidate.role || 'Professional'}</p>
                        {candidate.currentCompany && (
                            <p className="text-sm text-slate-500 flex items-center gap-1.5">
                                <Building2 className="w-4 h-4" />
                                {candidate.currentCompany}
                            </p>
                        )}
                        <div className="flex items-center gap-2 bg-emerald-50 text-emerald-700 px-3 py-2 rounded-lg w-fit">
                            <CheckCircle className="w-4 h-4" />
                            <span className="text-sm font-semibold">Unlocked</span>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                        {/* Main Column */}
                        <div className="lg:col-span-8 space-y-6">
                            {/* Education Section - All Education Entries */}
                            {candidate.educations && candidate.educations.length > 0 ? (
                                <div className="bg-gradient-to-br from-slate-50 to-slate-100/50 p-5 rounded-2xl border border-slate-200">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="p-3 bg-white rounded-xl shadow-sm border border-slate-100">
                                            <GraduationCap className="w-6 h-6 text-slate-700" />
                                        </div>
                                        <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Education</h3>
                                    </div>
                                    <div className="space-y-3">
                                        {candidate.educations.map((edu, idx) => (
                                            <div key={idx} className={`p-4 bg-white rounded-xl border border-slate-100 shadow-sm ${idx === 0 ? 'ring-1 ring-emerald-100' : ''}`}>
                                                <div className="flex items-start justify-between gap-4">
                                                    <div className="flex-1 min-w-0">
                                                        {edu.degree && (
                                                            <p className="font-semibold text-slate-900">
                                                                {edu.degree}{edu.field ? ` in ${edu.field}` : ''}
                                                            </p>
                                                        )}
                                                        {edu.uni && (
                                                            <p className="text-slate-600 text-sm mt-0.5">{edu.uni}</p>
                                                        )}
                                                    </div>
                                                    {formatDateRange(edu.start_year, edu.end_year) && (
                                                        <div className="text-xs text-slate-400 flex-shrink-0">
                                                            {formatDateRange(edu.start_year, edu.end_year)}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (candidate.university || candidate.degree) && (
                                <div className="bg-gradient-to-br from-slate-50 to-slate-100/50 p-5 rounded-2xl border border-slate-200">
                                    <div className="flex items-start gap-4">
                                        <div className="p-3 bg-white rounded-xl shadow-sm border border-slate-100">
                                            <GraduationCap className="w-6 h-6 text-slate-700" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Education</h3>
                                            {candidate.degree && (
                                                <p className="font-semibold text-slate-900 text-lg">{candidate.degree}</p>
                                            )}
                                            {candidate.university && (
                                                <p className="text-slate-600 mt-0.5">{candidate.university}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Unified Contact Section - Email, CV, LinkedIn */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                {/* Email */}
                                <button
                                    onClick={() => navigator.clipboard.writeText(candidate.email)}
                                    className="flex flex-col items-center justify-center p-4 bg-white border border-slate-200 hover:border-slate-300 hover:shadow-md rounded-xl transition-all group text-center"
                                >
                                    <div className="p-2.5 bg-slate-100 text-slate-600 rounded-lg mb-2 group-hover:scale-110 transition-transform">
                                        <Mail className="w-5 h-5" />
                                    </div>
                                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Email</span>
                                    <span className="text-sm font-medium text-slate-700 truncate max-w-full mt-0.5">{candidate.email}</span>
                                    <span className="text-[10px] text-slate-400 mt-1 flex items-center gap-1">
                                        <Copy className="w-3 h-3" /> Click to copy
                                    </span>
                                </button>

                                {/* CV */}
                                {candidate.cvUrl ? (
                                    <a
                                        href={candidate.cvUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex flex-col items-center justify-center p-4 bg-white border border-slate-200 hover:border-red-200 hover:shadow-md rounded-xl transition-all group text-center"
                                    >
                                        <div className="p-2.5 bg-red-50 text-red-600 rounded-lg mb-2 group-hover:scale-110 transition-transform">
                                            <Download className="w-5 h-5" />
                                        </div>
                                        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">CV / Resume</span>
                                        <span className="text-sm font-medium text-slate-700 mt-0.5">Download PDF</span>
                                        <span className="text-[10px] text-slate-400 mt-1 flex items-center gap-1">
                                            <ExternalLink className="w-3 h-3" /> Opens in new tab
                                        </span>
                                    </a>
                                ) : (
                                    <div className="flex flex-col items-center justify-center p-4 bg-slate-50 border border-slate-200 rounded-xl text-center opacity-60">
                                        <div className="p-2.5 bg-slate-100 text-slate-400 rounded-lg mb-2">
                                            <Download className="w-5 h-5" />
                                        </div>
                                        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">CV / Resume</span>
                                        <span className="text-sm font-medium text-slate-400 mt-0.5">Not available</span>
                                    </div>
                                )}

                                {/* LinkedIn */}
                                {candidate.linkedinUrl ? (
                                    <a
                                        href={candidate.linkedinUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex flex-col items-center justify-center p-4 bg-white border border-slate-200 hover:border-[#0A66C2]/30 hover:shadow-md rounded-xl transition-all group text-center"
                                    >
                                        <div className="p-2.5 bg-[#0A66C2]/10 text-[#0A66C2] rounded-lg mb-2 group-hover:scale-110 transition-transform">
                                            <Linkedin className="w-5 h-5" />
                                        </div>
                                        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">LinkedIn</span>
                                        <span className="text-sm font-medium text-slate-700 mt-0.5">View Profile</span>
                                        <span className="text-[10px] text-slate-400 mt-1 flex items-center gap-1">
                                            <ExternalLink className="w-3 h-3" /> Opens in new tab
                                        </span>
                                    </a>
                                ) : (
                                    <div className="flex flex-col items-center justify-center p-4 bg-slate-50 border border-slate-200 rounded-xl text-center opacity-60">
                                        <div className="p-2.5 bg-slate-100 text-slate-400 rounded-lg mb-2">
                                            <Linkedin className="w-5 h-5" />
                                        </div>
                                        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">LinkedIn</span>
                                        <span className="text-sm font-medium text-slate-400 mt-0.5">Not available</span>
                                    </div>
                                )}
                            </div>

                            {/* Key Details Row */}
                            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 p-4 sm:p-5 bg-slate-50 rounded-2xl border border-slate-100">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-white rounded-lg shadow-sm">
                                        <MapPin className="w-5 h-5 text-slate-500" />
                                    </div>
                                    <div>
                                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Location</p>
                                        <p className="font-semibold text-slate-900">{candidate.city}, {candidate.country}</p>
                                    </div>
                                </div>
                                {candidate.yearsExperience && (
                                    <>
                                        <div className="w-px h-10 bg-slate-200 hidden sm:block"></div>
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-white rounded-lg shadow-sm">
                                                <Briefcase className="w-5 h-5 text-slate-500" />
                                            </div>
                                            <div>
                                                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Experience</p>
                                                <p className="font-semibold text-slate-900">{candidate.yearsExperience} Years</p>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Summary */}
                            {candidate.summary && (
                                <div>
                                    <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-3">
                                        Professional Summary
                                    </h3>
                                    <p className="text-slate-700 leading-relaxed bg-white p-4 rounded-xl border border-slate-100">{candidate.summary}</p>
                                </div>
                            )}

                            {/* Skills */}
                            {candidate.skills && candidate.skills.length > 0 && (
                                <div>
                                    <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-3">
                                        Skills
                                    </h3>
                                    <div className="flex flex-wrap gap-2">
                                        {candidate.skills.map(skill => (
                                            <span
                                                key={skill}
                                                className="px-3 py-1.5 bg-white text-slate-700 rounded-lg text-sm font-medium border border-slate-200 shadow-sm"
                                            >
                                                {skill}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Experience Badges */}
                            {(candidate.hasStartupExperience || candidate.hasFoundedCompany || candidate.hasSideProjects) && (
                                <div>
                                    <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-3">
                                        Highlights
                                    </h3>
                                    <div className="flex flex-wrap gap-2">
                                        {candidate.hasStartupExperience && (
                                            <span className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 text-purple-700 rounded-full text-sm font-medium border border-purple-100">
                                                <Rocket className="w-3.5 h-3.5" />
                                                Startup Experience
                                            </span>
                                        )}
                                        {candidate.hasFoundedCompany && (
                                            <span className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 text-amber-700 rounded-full text-sm font-medium border border-amber-100">
                                                <Building2 className="w-3.5 h-3.5" />
                                                Founded Company
                                            </span>
                                        )}
                                        {candidate.hasSideProjects && (
                                            <span className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm font-medium border border-blue-100">
                                                <Lightbulb className="w-3.5 h-3.5" />
                                                Side Projects
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Sidebar */}
                        <div className="lg:col-span-4 space-y-6">
                            {/* Career Timeline */}
                            {candidate.companies && candidate.companies.length > 0 && (
                                <div className="bg-slate-50 rounded-2xl p-5 sm:p-6 border border-slate-100 flex flex-col max-h-[500px]">
                                    <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-5 flex items-center gap-2 flex-shrink-0">
                                        <Building2 className="w-4 h-4 text-slate-500" />
                                        Companies
                                    </h3>
                                    <div className="relative overflow-y-auto pr-2 custom-scrollbar">
                                        {/* Timeline line */}
                                        <div className="absolute left-[11px] top-2 bottom-2 w-0.5 bg-slate-200"></div>

                                        <div className="space-y-4">
                                            {candidate.companies.map((company, idx) => (
                                                <div key={idx} className="relative flex gap-4">
                                                    {/* Timeline dot */}
                                                    <div className={`relative z-10 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${idx === 0
                                                        ? 'bg-emerald-500 ring-4 ring-emerald-100'
                                                        : 'bg-white border-2 border-slate-300'
                                                        }`}>
                                                        {idx === 0 && <div className="w-2 h-2 bg-white rounded-full"></div>}
                                                    </div>

                                                    {/* Content */}
                                                    <div className="flex-1 pb-4">
                                                        <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm">
                                                            <p className="font-semibold text-slate-900 text-sm">{company.name}</p>
                                                            <p className="text-slate-600 text-sm mt-0.5">{company.role}</p>
                                                            {formatDateRange(company.start_year, company.end_year) && (
                                                                <p className="text-xs text-slate-400 mt-2">
                                                                    {formatDateRange(company.start_year, company.end_year)}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Matched For */}
                            <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100">
                                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-3">
                                    Matched For
                                </h3>
                                <div className="flex items-start gap-3">
                                    <div className="p-2 bg-white rounded-lg shadow-sm border border-slate-100">
                                        <Star className="w-5 h-5 text-slate-600" />
                                    </div>
                                    <div>
                                        <p className="font-semibold text-slate-900">{candidate.jobTitle}</p>
                                        <p className="text-xs text-slate-500 mt-1">This candidate was matched for this role</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer Actions */}
                <div className="flex-shrink-0 px-6 py-4 md:px-10 md:py-5 bg-slate-50 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="w-full sm:w-auto px-6 py-2.5 text-slate-600 hover:text-slate-900 font-medium rounded-xl transition-colors order-2 sm:order-1"
                    >
                        Close
                    </button>
                    <a
                        href={`mailto:${candidate.email}`}
                        className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-xl transition-colors order-1 sm:order-2"
                    >
                        <Mail className="w-4 h-4" />
                        Send Email
                    </a>
                </div>
            </div>
        </div>
    );
};

export const PurchasedCandidatesPage: React.FC = () => {
    const { user } = useAuth();
    const [searchParams] = useSearchParams();
    const [candidates, setCandidates] = useState<PurchasedCandidate[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterJobId, setFilterJobId] = useState<string>('');
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedCandidate, setSelectedCandidate] = useState<PurchasedCandidate | null>(null);
    const ITEMS_PER_PAGE = 15;

    // Check for payment success from URL params
    const paymentSuccess = searchParams.get('payment') === 'success';

    // Fetch purchased candidates
    useEffect(() => {
        if (!user) return;

        const fetchPurchasedCandidates = async () => {
            setLoading(true);
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/api/hirer/purchased-candidates`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ userData: { sub: user.id } })
                });

                const data = await response.json();
                if (data.success) {
                    setCandidates(data.candidates || []);
                }
            } catch (error) {
                console.error('Error fetching purchased candidates:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchPurchasedCandidates();
    }, [user]);

    // Filter candidates
    const filteredCandidates = candidates.filter(candidate => {
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            const matchesSearch = (
                candidate.name.toLowerCase().includes(query) ||
                candidate.email.toLowerCase().includes(query) ||
                candidate.role.toLowerCase().includes(query) ||
                candidate.jobTitle.toLowerCase().includes(query)
            );
            if (!matchesSearch) return false;
        }
        if (filterJobId && candidate.jobId !== filterJobId) return false;
        return true;
    });

    // Pagination
    const totalPages = Math.ceil(filteredCandidates.length / ITEMS_PER_PAGE);
    const paginatedCandidates = filteredCandidates.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    // Get unique jobs for filter dropdown
    const uniqueJobs = Array.from(
        new Map(
            candidates
                .filter(c => c.jobId)
                .map(c => [c.jobId, { id: c.jobId!, title: c.jobTitle }])
        ).values()
    );

    return (
        <div className="h-screen bg-slate-50 flex flex-col lg:overflow-hidden">
            <CompanySidebar />

            <div className="flex-1 flex flex-col lg:pl-72 min-w-0 overflow-y-auto lg:overflow-hidden">
                <div className="p-4 md:p-8 flex flex-col h-full w-full min-h-0">
                    {/* Success Banner */}
                    {paymentSuccess && (
                        <div className="mb-6 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl flex items-center gap-4 animate-fade-in shadow-sm">
                            <div className="p-2 bg-emerald-100 rounded-xl">
                                <CheckCircle className="w-6 h-6 text-emerald-600" />
                            </div>
                            <div>
                                <h3 className="font-bold text-emerald-900">Payment Successful!</h3>
                                <p className="text-sm text-emerald-700">Your purchased candidates are now available below with full contact details.</p>
                            </div>
                        </div>
                    )}

                    {/* Header */}
                    <div className="mb-6 mt-12 lg:mt-0 flex-shrink-0 animate-fade-in">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                            <div>
                                <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">Purchased Contacts</h1>
                                <p className="text-sm md:text-base text-slate-600">
                                    View and contact candidates you've unlocked.
                                </p>
                            </div>
                            {candidates.length > 0 && (
                                <div className="flex items-center gap-3">
                                    <div className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-xl border border-slate-200 shadow-sm">
                                        <Users className="w-5 h-5 text-slate-500" />
                                        <span className="font-bold text-slate-900">{candidates.length}</span>
                                        <span className="text-slate-500 text-sm">Unlocked</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Filters Bar */}
                    {candidates.length > 0 && (
                        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 md:p-5 mb-6 flex-shrink-0">
                            <div className="flex flex-col md:flex-row gap-4">
                                {/* Search */}
                                <div className="flex-1 relative">
                                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                    <input
                                        type="text"
                                        placeholder="Search by name, email, or role..."
                                        value={searchQuery}
                                        onChange={(e) => {
                                            setSearchQuery(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                        className="w-full pl-12 pr-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-slate-200 focus:border-slate-300 transition-colors"
                                    />
                                </div>

                                {/* Job Filter */}
                                {uniqueJobs.length > 0 && (
                                    <div className="w-full md:w-72">
                                        <select
                                            value={filterJobId}
                                            onChange={(e) => {
                                                setFilterJobId(e.target.value);
                                                setCurrentPage(1);
                                            }}
                                            className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-slate-200 focus:border-slate-300 bg-white transition-colors"
                                        >
                                            <option value="">All Job Postings</option>
                                            {uniqueJobs.map(job => (
                                                <option key={job.id} value={job.id}>{job.title}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Main Content */}
                    {loading ? (
                        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                            <TableSkeleton />
                        </div>
                    ) : candidates.length === 0 ? (
                        /* Empty State */
                        <div className="flex-1 flex items-center justify-center">
                            <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-12 text-center max-w-lg">
                                <div className="w-20 h-20 bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl flex items-center justify-center mx-auto mb-6">
                                    <Users className="w-10 h-10 text-slate-400" />
                                </div>
                                <h3 className="text-2xl font-bold text-slate-900 mb-3">No Contacts Yet</h3>
                                <p className="text-slate-600 mb-8 leading-relaxed">
                                    You haven't unlocked any candidate contacts yet. Browse matched candidates and unlock their details to connect with top talent.
                                </p>
                                <Link
                                    to="/candidates"
                                    className="inline-flex items-center gap-2 px-8 py-3.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-semibold transition-colors shadow-lg shadow-slate-900/10"
                                >
                                    <Sparkles className="w-5 h-5" />
                                    Browse Candidates
                                </Link>
                            </div>
                        </div>
                    ) : filteredCandidates.length === 0 ? (
                        /* No Results State */
                        <div className="flex-1 flex items-center justify-center">
                            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-12 text-center">
                                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <AlertCircle className="w-8 h-8 text-slate-400" />
                                </div>
                                <h3 className="text-xl font-semibold text-slate-900 mb-2">No Results Found</h3>
                                <p className="text-slate-600">
                                    No candidates match your search criteria. Try adjusting your filters.
                                </p>
                            </div>
                        </div>
                    ) : (
                        /* Candidates Table */
                        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden flex flex-col flex-1 min-h-0">
                            {/* Table Header */}
                            <div className="hidden lg:grid grid-cols-12 gap-4 px-6 py-4 bg-slate-50 border-b border-slate-100 text-xs font-bold text-slate-600 uppercase tracking-wide flex-shrink-0">
                                <div className="col-span-3">Candidate</div>
                                <div className="col-span-2">Current Role</div>
                                <div className="col-span-2">Company</div>
                                <div className="col-span-2">Matched Job</div>
                                <div className="col-span-1">Location</div>
                                <div className="col-span-2 text-right">Actions</div>
                            </div>

                            {/* Table Body */}
                            <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
                                {paginatedCandidates.map((candidate) => (
                                    <div
                                        key={candidate.id}
                                        className="hover:bg-slate-50 transition-colors cursor-pointer"
                                        onClick={() => setSelectedCandidate(candidate)}
                                    >
                                        {/* Desktop Row */}
                                        <div className="hidden lg:grid grid-cols-12 gap-4 px-6 py-5 items-center">
                                            {/* Candidate Info */}
                                            <div className="col-span-3 flex items-center gap-4">
                                                <div className="w-12 h-12 bg-gradient-to-br from-slate-200 to-slate-300 rounded-full flex items-center justify-center text-slate-700 font-bold text-lg flex-shrink-0">
                                                    {candidate.name.split(' ').map(n => n[0]).join('')}
                                                </div>
                                                <div className="min-w-0">
                                                    <h4 className="font-bold text-slate-900 truncate">{candidate.name}</h4>
                                                    <p className="text-sm text-slate-500 truncate">{candidate.email}</p>
                                                </div>
                                            </div>

                                            {/* Role */}
                                            <div className="col-span-2">
                                                {candidate.role && (
                                                    <span className="text-sm text-slate-700">{candidate.role}</span>
                                                )}
                                            </div>

                                            {/* Company */}
                                            <div className="col-span-2">
                                                <span className="text-sm text-slate-700 truncate block">{candidate.currentCompany || '-'}</span>
                                            </div>

                                            {/* Matched Job */}
                                            <div className="col-span-2">
                                                <span className="text-sm text-slate-700 truncate block">{candidate.jobTitle}</span>
                                            </div>

                                            {/* Location */}
                                            <div className="col-span-1 flex items-center gap-1 text-sm text-slate-600">
                                                <MapPin className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                                                <span className="truncate">{candidate.city}</span>
                                            </div>

                                            {/* Actions */}
                                            <div className="col-span-2 flex items-center justify-end gap-2" onClick={e => e.stopPropagation()}>
                                                <button
                                                    onClick={() => setSelectedCandidate(candidate)}
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-all shadow-sm"
                                                    title="View Details"
                                                >
                                                    <Eye className="w-3.5 h-3.5" />
                                                    View
                                                </button>
                                                <a
                                                    href={`mailto:${candidate.email}`}
                                                    className="p-2.5 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                                                    title="Send Email"
                                                >
                                                    <Mail className="w-4 h-4" />
                                                </a>
                                                {candidate.linkedinUrl && (
                                                    <a
                                                        href={candidate.linkedinUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="p-2.5 text-[#0A66C2] hover:bg-blue-50 rounded-lg transition-colors"
                                                        title="View LinkedIn"
                                                    >
                                                        <Linkedin className="w-4 h-4" />
                                                    </a>
                                                )}
                                                {candidate.cvUrl && (
                                                    <a
                                                        href={candidate.cvUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="p-2.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                        title="Download CV"
                                                    >
                                                        <Download className="w-4 h-4" />
                                                    </a>
                                                )}
                                            </div>
                                        </div>

                                        {/* Mobile Card */}
                                        <div className="lg:hidden p-4 space-y-3">
                                            <div className="flex items-start gap-3">
                                                <div className="w-14 h-14 bg-gradient-to-br from-slate-200 to-slate-300 rounded-xl flex items-center justify-center text-slate-700 font-bold text-lg flex-shrink-0">
                                                    {candidate.name.split(' ').map(n => n[0]).join('')}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <h4 className="font-bold text-slate-900 text-lg">{candidate.name}</h4>
                                                    <p className="text-sm text-slate-600 truncate">{candidate.email}</p>
                                                    {candidate.role && (
                                                        <p className="text-sm text-slate-500 mt-1">{candidate.role}</p>
                                                    )}
                                                    {candidate.currentCompany && (
                                                        <p className="text-sm text-slate-500 flex items-center gap-1 mt-0.5">
                                                            <Building2 className="w-3.5 h-3.5" />
                                                            {candidate.currentCompany}
                                                        </p>
                                                    )}
                                                </div>
                                                <ChevronRight className="w-5 h-5 text-slate-400 mt-1" />
                                            </div>

                                            <div className="flex flex-wrap gap-2">
                                                <span className="px-2.5 py-1.5 bg-slate-100 text-slate-600 text-xs font-semibold rounded-lg flex items-center gap-1.5">
                                                    <Briefcase className="w-3.5 h-3.5" />
                                                    {candidate.jobTitle}
                                                </span>
                                                <span className="px-2.5 py-1.5 bg-slate-100 text-slate-600 text-xs font-semibold rounded-lg flex items-center gap-1.5">
                                                    <MapPin className="w-3.5 h-3.5" />
                                                    {candidate.city}
                                                </span>
                                            </div>

                                            <div className="flex flex-col gap-2 pt-3 border-t border-slate-100" onClick={e => e.stopPropagation()}>
                                                <button
                                                    onClick={() => setSelectedCandidate(candidate)}
                                                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-50 text-blue-700 text-sm font-bold rounded-xl border border-blue-100 hover:bg-blue-100 transition-all mb-1"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                    View Details
                                                </button>
                                                <div className="flex gap-2">
                                                    <a
                                                        href={`mailto:${candidate.email}`}
                                                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-900 text-white text-sm font-semibold rounded-xl"
                                                    >
                                                        <Mail className="w-4 h-4" />
                                                        Email
                                                    </a>
                                                    {candidate.linkedinUrl && (
                                                        <a
                                                            href={candidate.linkedinUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="p-2.5 bg-[#0A66C2] text-white rounded-xl flex items-center justify-center"
                                                        >
                                                            <Linkedin className="w-4 h-4" />
                                                        </a>
                                                    )}
                                                    {candidate.cvUrl && (
                                                        <a
                                                            href={candidate.cvUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="p-2.5 bg-red-600 text-white rounded-xl flex items-center justify-center"
                                                        >
                                                            <Download className="w-4 h-4" />
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex flex-col md:flex-row items-center justify-between gap-4 flex-shrink-0">
                                    <p className="text-sm text-slate-600 order-2 md:order-1">
                                        Showing <span className="font-bold">{(currentPage - 1) * ITEMS_PER_PAGE + 1}</span> to{' '}
                                        <span className="font-bold">{Math.min(currentPage * ITEMS_PER_PAGE, filteredCandidates.length)}</span> of{' '}
                                        <span className="font-bold">{filteredCandidates.length}</span>
                                    </p>
                                    <div className="flex gap-2 order-1 md:order-2">
                                        <button
                                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                            disabled={currentPage === 1}
                                            className="p-2.5 border border-slate-200 rounded-xl hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors bg-white"
                                        >
                                            <ChevronLeft className="w-5 h-5" />
                                        </button>
                                        <span className="px-5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-bold">
                                            {currentPage} / {totalPages}
                                        </span>
                                        <button
                                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                            disabled={currentPage === totalPages}
                                            className="p-2.5 border border-slate-200 rounded-xl hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors bg-white"
                                        >
                                            <ChevronRight className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Candidate Detail Modal */}
            {selectedCandidate && (
                <CandidateDetailModal
                    candidate={selectedCandidate}
                    onClose={() => setSelectedCandidate(null)}
                />
            )}
        </div>
    );
};
